import { useMutation, useQueryClient } from 'react-query'
import { useSvcCompareVersionsContext } from '@dc-shared-web/providers'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import {
  resolveChange,
  ResolveChangeParams,
} from '@dc-shared-web/hooks/api/comparison/resolveChange'
import { COMPARISON_DATA_QUERY_KEY } from '@dc-shared-web/hooks/api/queryKeys'
import { ComparisonDataResponse } from '@dc-shared-web/hooks/api/comparison/types'

interface MutationContext {
  previousComparisonData?: ComparisonDataResponse
}

export const useResolveChange = (
  versionAGuid: string,
  versionBGuid: string
) => {
  const svcCompareVersionApiClient = useSvcCompareVersionsContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation<
    ResolveChangeParams,
    Error,
    ResolveChangeParams,
    MutationContext
  >(
    (params: ResolveChangeParams) =>
      resolveChange(svcCompareVersionApiClient, params),
    {
      onMutate: async (params: ResolveChangeParams) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        const queryKey = [COMPARISON_DATA_QUERY_KEY, versionAGuid, versionBGuid]
        await queryClient.cancelQueries(queryKey)

        // Snapshot the previous value
        const previousComparisonData =
          queryClient.getQueryData<ComparisonDataResponse>(queryKey)

        // Optimistically update the cache
        if (previousComparisonData) {
          const updatedData = {
            ...previousComparisonData,
            data: {
              ...previousComparisonData.data,
              changes: previousComparisonData.data.changes.map((change) =>
                change.uuid === params.uuid
                  ? { ...change, is_resolved: params.is_resolved }
                  : change
              ),
            },
          }
          queryClient.setQueryData(queryKey, updatedData)
        }

        // Return a context object with the snapshotted value
        return { previousComparisonData }
      },
      onError: (_error, _params, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        const queryKey = [COMPARISON_DATA_QUERY_KEY, versionAGuid, versionBGuid]
        if (context?.previousComparisonData) {
          queryClient.setQueryData(queryKey, context.previousComparisonData)
        }

        toast({
          title: 'Error updating change status',
          description: 'If the issue persists, please contact support.',
          variant: 'error',
        })
      },
      onSuccess: (_data, params) => {
        toast({
          variant: 'success',
          title: params.is_resolved
            ? 'Change marked as resolved'
            : 'Change marked as unresolved',
        })
      },
      onSettled: () => {
        // Always refetch after error or success to ensure we have the latest data
        queryClient.invalidateQueries([
          COMPARISON_DATA_QUERY_KEY,
          versionAGuid,
          versionBGuid,
        ])
      },
    }
  )
}
